import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Dumbbell,
  Coffee,
  Sparkles,
  Store,
  Mountain,
  Megaphone,
  BarChart3,
  Palette,
  Shield,
  Zap,
  Play,
  Pause,
  Bean,
  Rocket,
  Volume2,
  VolumeX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LANDING } from "@/constants/testIds";
import { LandingCurrencySwitcher, useLandingCurrency } from "@/components/LandingCurrencySwitcher";
import { useFxRates } from "@/components/FxRatesProvider";
import { HeroMediaCarousel } from "@/components/HeroMediaCarousel";
import { formatMoney, convertFromUSD } from "@/lib/currency";
import ChatWidget from "@/components/ChatWidget";

/* ---------- Media assets ---------- */
// Videos are self-hosted (public/videos/*) — no third-party dependency.
// Two codecs: WebM/VP9 (open, all Chromium/Firefox) + H.264 MP4 (Safari + Chrome).
const VIDEOS = {
  heroCoffee: { webm: "/videos/espresso.webm", mp4: "/videos/espresso.mp4" },
  fitness:    { webm: "/videos/fitness.webm",  mp4: "/videos/fitness.mp4"  },
  roastery:   { webm: "/videos/beans.webm",    mp4: "/videos/beans.mp4"    },
  latteArt:   { webm: "/videos/latte.webm",    mp4: "/videos/latte.mp4"    },
  barista: {
    mp4: "https://customer-assets-0z36b82j.emergentagent.net/job_business-wellness-1/artifacts/ixme6r7y_BARISTA.mp4",
  },
};

// Unsplash posters (also used as still images throughout the page)
const IMG = {
  studio:
    "https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  cafeInterior:
    "https://images.unsplash.com/photo-1511081692775-05d0f180a065?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  retreat:
    "https://images.unsplash.com/photo-1543858828-7cf1a9beb95c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  coach:
    "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  pourover:
    "https://images.unsplash.com/photo-1608552265232-3a1e3feb0271?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  beans:
    "https://images.unsplash.com/photo-1447933601403-0c6688de566e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  latteArt:
    "https://images.unsplash.com/photo-1541167760496-1628856ab772?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  roastery:
    "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  espressoMachine:
    "https://images.unsplash.com/photo-1509785307050-d4066910ec1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  gymFloor:
    "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  barista:
    "https://images.unsplash.com/photo-1442512595331-e89e73853f31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  cafeCounter:
    "https://images.unsplash.com/photo-1521017432531-fbd92d768814?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

/* ---------- Utilities ---------- */

function Reveal({ children, delay = 0, y = 20, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Robust video component — plays a background loop, falls back to poster if
 * the network / codec fails. Also exposes play/pause + mute controls.
 * When falling back to the poster image we apply a Ken-Burns effect so the
 * hero still feels alive.
 */
function LoopingVideo({ src, poster, className = "", controls = false, ariaLabel }) {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const handleError = () => {
    // <source>-level errors also bubble up to the video element. Only treat
    // it as fatal if NONE of the sources could load (readyState still 0).
    const v = ref.current;
    if (v && v.readyState > 0) return;
    setFailed(true);
  };

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div className={"relative overflow-hidden " + className}>
      {!failed ? (
        <video
          ref={ref}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={ariaLabel}
          onError={handleError}
          className="h-full w-full object-cover ken-burns-slow"
        >
          {src?.webm && <source src={src.webm} type="video/webm" />}
          {src?.mp4 && <source src={src.mp4} type='video/mp4; codecs="avc1.42E01E"' />}
        </video>
      ) : (
        <img
          src={poster}
          alt={ariaLabel || ""}
          className="h-full w-full object-cover ken-burns"
        />
      )}

      {controls && !failed && (
        <div className="absolute bottom-3 right-3 flex gap-1.5">
          <button
            onClick={togglePlay}
            className="h-8 w-8 grid place-items-center rounded-full bg-background/70 backdrop-blur text-foreground hover:bg-background transition-colors"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={toggleMute}
            className="h-8 w-8 grid place-items-center rounded-full bg-background/70 backdrop-blur text-foreground hover:bg-background transition-colors"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}

const MODULES = [
  { key: "fitness", name: "Fitness Coaching", icon: Dumbbell, blurb: "Clients, workouts, nutrition, AI programming.", live: true, img: IMG.gymFloor },
  { key: "cafe", name: "Café Operations", icon: Coffee, blurb: "POS, kitchen board, menu, tables, loyalty.", live: true, img: IMG.espressoMachine },
  { key: "roastery", name: "Coffee Roastery", icon: Bean, blurb: "Green stock, roast profiles, wholesale.", live: true, img: IMG.roastery },
  { key: "ai", name: "AI Automation", icon: Sparkles, blurb: "Forecasts, marketing, generation, insights.", live: true, img: IMG.pourover },
  { key: "retail", name: "Retail Store", icon: Store, blurb: "Products, inventory, shipping, coupons.", img: IMG.cafeCounter },
  { key: "retreats", name: "Retreat Management", icon: Mountain, blurb: "Events, bookings, schedules, waivers.", img: IMG.retreat },
  { key: "marketing", name: "Marketing CRM", icon: Megaphone, blurb: "Leads, funnels, email, SMS, WhatsApp.", img: IMG.barista },
  { key: "growth", name: "Growth Engine", icon: Rocket, blurb: "B2B lead discovery, AI qualification, personalized outreach.", live: true, isNew: true, img: IMG.roastery },
  { key: "whitelabel", name: "White Label", icon: Palette, blurb: "Custom logo, domain, colors, mobile app.", live: true, img: IMG.latteArt },
];

const AUDIENCES = [
  "Personal Trainers", "Online Coaches", "Yoga Studios", "CrossFit Boxes",
  "Boutique Gyms", "Coffee Shops", "Specialty Roasters", "Retreat Operators",
  "Health Resorts", "Hybrid Brands", "Wellness Cafés", "Nutritionists",
];

/* ---------- Page ---------- */

export default function Landing() {
  // Parallax on hero
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 600], [0, -60]);
  const heroTextY = useTransform(scrollY, [0, 600], [0, -30]);
  // Public visitor's currency for pricing (persists via localStorage).
  const currency = useLandingCurrency();
  // Subscribe to live FX rates so pricing rerenders once the fetch resolves.
  const fx = useFxRates();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-lg leading-none">K</div>
            <div className="font-display text-xl">Kairosa</div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#modules" className="hover:text-foreground transition-colors">Modules</a>
            <a href="#coffee" className="hover:text-foreground transition-colors">Coffee & Roastery</a>
            <a href="#fitness" className="hover:text-foreground transition-colors">Studio & Coaching</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <LandingCurrencySwitcher />
            <Link to="/login" data-testid={LANDING.navLogin}>
              <Button variant="ghost" size="sm" className="rounded-full">Sign in</Button>
            </Link>
            <Link to="/register" data-testid={LANDING.navRegister}>
              <Button size="sm" className="rounded-full gap-1">
                Start free <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ============ HERO — Video-first, coffee + fitness equal weight ============ */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="brand-glow absolute inset-0 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 pt-20 md:pt-24 pb-16 md:pb-20 grid md:grid-cols-12 gap-8 md:gap-10 items-center relative">
          {/* Text */}
          <motion.div style={{ y: heroTextY }} className="md:col-span-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground border border-border rounded-full px-3 py-1.5 mb-8">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                The Jay Project · v1.0
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-[2rem] sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight tracking-tight">
                {/* Rotating value-prop carousel — one phrase visible at a time,
                    fixed-height container so nothing shifts underneath.
                    aria-hidden on the visual rotation; screen-readers get the
                    static label below. */}
                <span
                  className="hero-carousel"
                  data-testid="hero-carousel"
                  aria-hidden="true"
                >
                  <span className="hero-carousel__track">
                    <span className="hero-carousel__item">Pour espresso</span>
                    <span className="hero-carousel__item">Design training</span>
                    <span className="hero-carousel__item">Curate wellness flows</span>
                    <span className="hero-carousel__item">Grow communities</span>
                    {/* Duplicate of the first item so the wrap-around is seamless. */}
                    <span className="hero-carousel__item">Pour espresso</span>
                  </span>
                </span>
                <span className="sr-only">
                  Pour espresso. Design training. Curate wellness flows. Grow communities. Run one business.
                </span>
                <em className="not-italic block mt-2 font-semibold gradient-violet-teal">
                  Run one business.
                </em>
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-xl text-lg text-muted-foreground leading-relaxed">
                Kairosa is the operating system for wellness &amp; coffee entrepreneurs — coach
                clients, roast beans, run a café, launch retreats, and grow a brand from a single,
                beautifully connected dashboard.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/register" data-testid={LANDING.ctaGetStarted}>
                  <Button size="lg" className="rounded-full gap-2 px-6 h-12">
                    Start your business <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/login" data-testid={LANDING.ctaSignIn}>
                  <Button size="lg" variant="outline" className="rounded-full px-6 h-12">
                    See the demo
                  </Button>
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.32}>
              <div
                data-testid="landing-demo-credentials"
                className="mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.22em] text-muted-foreground border border-border rounded-full px-4 py-2"
              >
                <span className="text-foreground/80">Try the demo</span>
                <span className="opacity-40">·</span>
                <code className="font-mono normal-case tracking-normal text-foreground">
                  owner@luminawellness.com
                </code>
                <span className="opacity-40">/</span>
                <code className="font-mono normal-case tracking-normal text-foreground">
                  Demo123!
                </code>
              </div>
            </Reveal>
          </motion.div>

          {/* Video collage */}
          <motion.div style={{ y: heroImgY }} className="md:col-span-6 relative">
            <div className="grid grid-cols-6 gap-3">
              <LoopingVideo
                src={VIDEOS.heroCoffee}
                poster={IMG.espressoMachine}
                ariaLabel="Espresso pour"
                className="col-span-3 aspect-[4/5] rounded-2xl border border-border"
              />
              <HeroMediaCarousel
                className="col-span-3 aspect-[4/5] rounded-2xl border border-border"
                items={[
                  { type: "video", src: "/videos/hero/hero-1.mp4", poster: "/img/hero/hero-1-poster.jpg", alt: "San B's studio flow" },
                  { type: "video", src: "/videos/hero/hero-2.mp4", poster: "/img/hero/hero-2-poster.jpg", alt: "Community class" },
                  { type: "image", src: "/img/hero/hero-4.webp", alt: "Studio community — Namaste" },
                  { type: "video", src: "/videos/hero/hero-3.mp4", poster: "/img/hero/hero-3-poster.jpg", alt: "San B's — full session", maxMs: 15000 },
                ]}
              />
              <LoopingVideo
                src={VIDEOS.barista}
                poster={IMG.barista}
                ariaLabel="Barista pulling espresso shots — live"
                className="col-span-6 aspect-[16/6] rounded-2xl border border-border"
              />
            </div>
            <div className="absolute -bottom-3 left-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground bg-background/80 backdrop-blur border border-border rounded-full px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" /> Live
            </div>
          </motion.div>
        </div>

        {/* Ticker */}
        <div className="border-y border-border overflow-hidden">
          <div className="flex ticker-track whitespace-nowrap py-4 text-sm text-muted-foreground">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex shrink-0 items-center gap-10 pr-10">
                {AUDIENCES.map((t) => (
                  <span key={t + i} className="inline-flex items-center gap-3">
                    <span className="h-1 w-1 rounded-full bg-primary" />
                    <span className="uppercase tracking-[0.22em] text-xs">{t}</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ COFFEE + ROASTERY DEDICATED SECTION ============ */}
      <section id="coffee" className="relative border-b border-border overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-3">02 · Coffee & Roastery</div>
                <h2 className="font-display text-4xl sm:text-5xl leading-tight max-w-xl">
                  From green bean to<br />signature pour.
                </h2>
              </div>
              <p className="text-muted-foreground max-w-md">
                POS, wholesale, roast profiles, QR ordering, kitchen board, loyalty and specialty
                coffee retail — engineered by people who actually work behind the bar.
              </p>
            </div>
          </Reveal>

          {/* Feature video + stat cards */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <Reveal className="lg:col-span-8">
              <LoopingVideo
                src={VIDEOS.latteArt}
                poster={IMG.latteArt}
                ariaLabel="Latte art"
                controls
                className="aspect-[16/9] rounded-2xl border border-border"
              />
            </Reveal>
            <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4">
              {[
                { k: "24", label: "Second POS checkout" },
                { k: "3×", label: "Faster kitchen turnaround" },
                { k: "∞", label: "Roast profiles per SKU" },
              ].map((s, i) => (
                <Reveal key={s.label} delay={0.05 * i}>
                  <div className="rounded-2xl border border-border bg-card p-6 h-full flex flex-col justify-between">
                    <div className="font-display text-5xl leading-none">{s.k}</div>
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mt-6">
                      {s.label}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Coffee photo carousel */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[IMG.pourover, IMG.beans, IMG.espressoMachine, IMG.cafeCounter].map((src, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="aspect-[4/5] rounded-xl overflow-hidden border border-border">
                  <img src={src} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FITNESS SECTION ============ */}
      <section id="fitness" className="relative border-b border-border bg-secondary/30 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <LoopingVideo
              src={VIDEOS.fitness}
              poster={IMG.coach}
              ariaLabel="Coach and client"
              className="aspect-[4/5] rounded-2xl border border-border"
            />
          </Reveal>
          <div>
            <Reveal>
              <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-3">03 · Studio & Coaching</div>
              <h2 className="font-display text-4xl sm:text-5xl leading-tight mb-6">
                Programming that pours itself.
              </h2>
              <p className="text-muted-foreground max-w-lg">
                Claude-powered workout generation, drag-and-drop workout builder, client progress
                tracking, and an exercise library your team will actually use. No spreadsheets.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {["AI workout designer", "Client roster & progress", "Exercise video library", "Nutrition (v1.1)", "Habit tracking (v1.1)", "Attendance & bookings"].map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-primary" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ PHILOSOPHY ============ */}
      <section id="philosophy" className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-12 gap-x-10 gap-y-6 items-start">
        <Reveal className="md:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-3">04 · Philosophy</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight">
            Every feature answers one question.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7 md:pl-4">
          <p className="text-2xl font-display leading-relaxed text-foreground/90">
            <em className="not-italic">“Does this help a wellness entrepreneur run and grow their business from one unified platform?”</em>
          </p>
          <p className="mt-8 text-base text-muted-foreground max-w-2xl">
            Coaching software isn't enough. POS isn't enough. Marketing tools aren't enough.
            Kairosa is the connective tissue between how you serve clients, sell coffee, host
            retreats, and build a brand — designed like Apple, priced like a startup,
            engineered like Stripe.
          </p>
        </Reveal>
      </section>

      {/* ============ MODULES bento with imagery ============ */}
      <section id="modules" className="border-y border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-3">05 · Modules</div>
                <h2 className="font-display text-4xl sm:text-5xl leading-tight max-w-xl">
                  Subscribe to what you need. Add more anytime.
                </h2>
              </div>
              <p className="text-muted-foreground max-w-md">
                Every module is a self-contained SaaS. Toggle instantly — your data, team, and
                branding stay connected across all of them.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {MODULES.map((m, i) => (
              <Reveal key={m.key} delay={i * 0.05}>
                <div
                  data-testid={LANDING.moduleTile(m.key)}
                  className="group relative rounded-xl border border-border bg-background overflow-hidden hover:-translate-y-1 transition-transform h-full"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={m.img}
                      alt=""
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <m.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                      <div className="flex items-center gap-1.5">
                        {m.isNew && (
                          <span className="text-[9px] uppercase tracking-[0.22em] bg-accent/25 text-accent-foreground border border-accent/40 px-1.5 py-0.5 rounded-full">New</span>
                        )}
                        {m.live ? (
                          <span className="text-[9px] uppercase tracking-[0.22em] text-primary">Live</span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Soon</span>
                        )}
                      </div>
                    </div>
                    <div className="font-display text-xl leading-tight mb-1">{m.name}</div>
                    <div className="text-sm text-muted-foreground leading-relaxed">{m.blurb}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Feature strip ============ */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-16 grid md:grid-cols-3 gap-8">
          {[
            { icon: Shield, title: "Enterprise multi-tenant", body: "Isolated data, RBAC roles, audit-ready." },
            { icon: Palette, title: "Custom domain + white label", body: "Your logo, colors, and app.mybrand.com." },
            { icon: Zap, title: "AI-native workflow", body: "Claude Sonnet 4.5 embedded in every module." },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="flex gap-4">
                <div className="h-10 w-10 shrink-0 rounded-md border border-border grid place-items-center">
                  <f.icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="font-display text-xl">{f.title}</div>
                  <div className="text-sm text-muted-foreground mt-1">{f.body}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ Pricing ============ */}
      <section id="pricing" className="mx-auto max-w-7xl px-6 py-24">
        <Reveal>
          <div className="text-center mb-14">
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-3">06 · Pricing</div>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">Start free. Talk to us for the rest.</h2>
            <p className="text-muted-foreground text-sm mt-3 max-w-lg mx-auto">
              Solo is a 30-day free trial you can start yourself. Studio and Empire are priced per business —
              share a few details and we'll come back with a fit-for-purpose plan within 24 hours.
            </p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { name: "Solo", usd: 0, meta: "for the first 30 days", features: ["Core Platform", "1 module", "1 team seat", "Community support"] },
            { name: "Studio", talkToSales: true, meta: "for wellness operators — priced per business", features: ["Core Platform", "3 modules of your choice", "3 team seats", "AI Assistant (Claude Sonnet 4.5)", "Growth Engine — B2B lead intelligence"], recommended: true, badge: "Includes Growth Engine" },
            { name: "Empire", talkToSales: true, meta: "multi-location + custom apps", features: ["All modules + Growth Engine", "White label", "Custom domain", "Priority support"] },
          ].map((p, i) => {
            // Solo — free trial. Every other plan is now "Talk to Sales" — no
            // numeric price on the landing. Prospects submit an enquiry that
            // emails the founder for a fit-for-purpose quote.
            const inrRate = fx?.rates?.INR;
            const usdFromINR = p.baseINR && inrRate ? p.baseINR / inrRate : null;
            const displayPrice =
              p.talkToSales
                ? "Talk to sales"
                : p.usd === 0
                ? "Free"
                : p.baseINR != null
                ? currency === "INR"
                  ? formatMoney(p.baseINR, "INR", { maximumFractionDigits: 0 })
                  : usdFromINR != null
                  ? formatMoney(convertFromUSD(usdFromINR, currency), currency, { maximumFractionDigits: 0 })
                  : "—"
                : formatMoney(convertFromUSD(p.usd, currency), currency, { maximumFractionDigits: 0 });
            return (
            <Reveal key={p.name} delay={i * 0.08}>
              <div className={"rounded-xl border p-8 relative h-full " + (p.recommended ? "border-primary bg-primary/5" : "border-border bg-card")}>
                {p.recommended && (
                  <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.22em] bg-primary text-primary-foreground px-2 py-1 rounded-full">Recommended</div>
                )}
                <div className="font-display text-3xl">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <div className={p.talkToSales ? "font-display text-3xl" : "font-display text-5xl"} data-testid={`landing-price-${p.name.toLowerCase()}`}>{displayPrice}</div>
                </div>
                <div className="text-xs text-muted-foreground mb-6">
                  {p.meta}
                  {!p.talkToSales && p.usd > 0 && currency !== "USD" && (
                    <span className="ml-1 opacity-70">· live-converted from ${p.usd} USD</span>
                  )}
                  {!p.talkToSales && p.baseINR != null && currency !== "INR" && (
                    <span className="ml-1 opacity-70">· live-converted from ₹{p.baseINR} INR</span>
                  )}
                </div>
                <ul className="space-y-2 text-sm">
                  {p.features.map((f) => {
                    const isGrowth = /Growth Engine/i.test(f);
                    return (
                      <li key={f} className="flex items-center gap-2">
                        <span className={"h-1 w-1 rounded-full " + (isGrowth ? "bg-accent" : "bg-primary")} />
                        <span className={isGrowth ? "font-medium" : ""}>{f}</span>
                        {isGrowth && (
                          <span className="ml-1 text-[9px] uppercase tracking-[0.22em] bg-accent/20 text-accent-foreground border border-accent/40 px-1.5 py-0.5 rounded-full">
                            New
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <Link to={
                  p.name === "Solo" ? "/register?plan=solo"
                  : p.name === "Studio" ? "/empire-enquiry?plan=studio"
                  : "/empire-enquiry?plan=empire"
                }>
                  <Button data-testid={`landing-cta-${p.name.toLowerCase()}`} className="w-full mt-8 rounded-full" variant={p.recommended ? "default" : "outline"}>
                    {p.talkToSales ? "Talk to sales" : "Get started"}
                  </Button>
                </Link>
              </div>
            </Reveal>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col md:flex-row justify-between text-sm text-muted-foreground gap-4">
          <div>© {new Date().getFullYear()} Kairosa — One Platform. Infinite Wellness Businesses.</div>
          <div className="flex gap-6 items-center">
            <span>The Jay Project</span>
            <span>v1.0 · MVP</span>
          </div>
        </div>
      </footer>
      <ChatWidget />
    </div>
  );
}
